import {
  Avatar,
  Box,
  Tooltip as MuiTooltip,
  Typography,
  styled,
} from '@mui/material'
import { memo, useMemo } from 'react'
import { SelectOption } from '../../Kanban'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'

type Props = {
  accountable: SelectOption | null | undefined
  responsible: SelectOption | null | undefined
  assignee: SelectOption | null | undefined
}

type UserRole = {
  user: SelectOption | null | undefined
  roleKey: string
}

const tooltipSlotProps = {
  tooltip: {
    sx: {
      backgroundColor: colorPalette.monotone[0],
      color: colorPalette.monotone[10],
      fontSize: '12px',
      padding: '8px 12px',
      borderRadius: '4px',
      boxShadow: 'none',
      filter: 'drop-shadow(0px 2px 6px rgba(123, 140, 170, 0.4))',
    },
  },
  arrow: {
    sx: {
      color: colorPalette.monotone[0],
    },
  },
}

const TooltipRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  height: '20px',
})

const TooltipContent = memo(({ roles }: { roles: UserRole[] }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {roles.map((role, index) => (
        <TooltipRow key={index}>
          <Typography
            component="span"
            sx={{ fontSize: '12px', minWidth: '70px' }}
          >
            {intl.formatMessage({ id: role.roleKey })}:
          </Typography>
          {role.user ? (
            <>
              <Avatar
                src={role.user.iconUrl}
                sx={{
                  width: 16,
                  height: 16,
                  margin: 0,
                  fontSize: 10,
                  color: colorPalette.monotone[10],
                }}
              >
                {role.user.name?.charAt(0) ?? ''}
              </Avatar>
              <Typography component="span" sx={{ fontSize: '12px' }}>
                {role.user.name}
              </Typography>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  margin: 0,
                  borderRadius: '50%',
                  backgroundColor: colorPalette.monotone[0],
                  border: `1px solid ${colorPalette.monotone[2]}`,
                  boxSizing: 'border-box',
                }}
              />
              <Typography
                component="span"
                sx={{ fontSize: '12px', color: colorPalette.monotone[4] }}
              >
                {intl.formatMessage({ id: 'wbsItem.unassigned' })}
              </Typography>
            </>
          )}
        </TooltipRow>
      ))}
    </Box>
  )
})

const UserAvatar = memo(
  ({ user, sx }: { user: SelectOption | null | undefined; sx?: object }) => {
    if (!user) {
      return (
        <Box
          sx={{
            width: 24,
            height: 24,
            margin: 0,
            borderRadius: '50%',
            backgroundColor: colorPalette.monotone[0],
            border: `1px solid ${colorPalette.monotone[2]}`,
            boxSizing: 'border-box',
            ...sx,
          }}
        />
      )
    }

    return (
      <Avatar
        variant="circular"
        src={user.iconUrl}
        sx={{
          width: 24,
          height: 24,
          margin: 0,
          fontSize: 12,
          color: colorPalette.monotone[10],
          ...sx,
        }}
      >
        {user.name?.charAt(0) ?? ''}
      </Avatar>
    )
  }
)

export const UserAvatarsGroup = memo(
  ({ accountable, responsible, assignee }: Props) => {
    const roles: UserRole[] = useMemo(
      () => [
        { user: accountable, roleKey: 'wbsItem.accountable' },
        { user: responsible, roleKey: 'wbsItem.responsible' },
        { user: assignee, roleKey: 'wbsItem.assignee' },
      ],
      [accountable, responsible, assignee]
    )

    const tooltipContent = useMemo(
      () => <TooltipContent roles={roles} />,
      [roles]
    )

    return (
      <MuiTooltip
        title={tooltipContent}
        placement="top"
        arrow={true}
        slotProps={tooltipSlotProps}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <UserAvatar user={accountable} />
          <UserAvatar user={responsible} sx={{ ml: '-4px' }} />
          <UserAvatar user={assignee} sx={{ ml: '-4px' }} />
        </Box>
      </MuiTooltip>
    )
  }
)
